import { useState } from 'react'

interface Props {
  /** Where the OAuth/magic-link should land after success. */
  callbackURL: string
  /** Called when the magic link has been emailed. The page should swap to its "check your email" view. */
  onMagicLinkSent: (email: string) => void
}

/**
 * Sign-in form: GitHub OAuth button + magic-link email form.
 * Used by /workspace (ProjectList) and /device (DeviceApprove). Each page wraps
 * it with its own headings; this component only renders the controls.
 */
export function SignInPanel({ callbackURL, onMagicLinkSent }: Props) {
  const [email, setEmail] = useState('')
  const [githubLoading, setGithubLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [err, setErr] = useState('')

  async function signInWithGithub() {
    setErr('')
    setGithubLoading(true)
    try {
      const res = await fetch('/api/auth/sign-in/social', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider: 'github', callbackURL }),
      })
      const data = await res.json().catch(() => ({})) as { url?: string; error?: { message?: string }; message?: string }
      if (!res.ok) throw new Error(data.error?.message ?? data.message ?? `HTTP ${res.status}`)
      if (!data.url) throw new Error('No redirect URL returned')
      window.location.href = data.url
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not start GitHub sign-in')
      setGithubLoading(false)
    }
  }

  async function sendMagicLink(e: React.FormEvent) {
    e.preventDefault()
    const trimmed = email.trim()
    if (!trimmed) return
    setSending(true)
    setErr('')
    try {
      const res = await fetch('/api/auth/sign-in/magic-link', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed, callbackURL }),
      })
      const data = await res.json().catch(() => ({})) as { error?: { message?: string }; message?: string }
      if (!res.ok) throw new Error(data.error?.message ?? data.message ?? `HTTP ${res.status}`)
      onMagicLinkSent(trimmed)
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to send link')
    } finally {
      setSending(false)
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={signInWithGithub}
        disabled={githubLoading}
        className="btn btn-ghost btn-lg"
        style={{ width: '100%', justifyContent: 'center', gap: 10, marginBottom: 18 }}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
          <path d="M8 0C3.58 0 0 3.58 0 8a8 8 0 0 0 5.47 7.59c.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.01 8.01 0 0 0 16 8c0-4.42-3.58-8-8-8z"/>
        </svg>
        {githubLoading ? 'Redirecting…' : 'Continue with GitHub'}
      </button>

      <div className="row" style={{ alignItems: 'center', gap: 12, marginBottom: 18 }}>
        <div style={{ flex: 1, height: 1, background: 'var(--line-soft)' }} />
        <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)', letterSpacing: '0.08em' }}>OR</span>
        <div style={{ flex: 1, height: 1, background: 'var(--line-soft)' }} />
      </div>

      <form onSubmit={sendMagicLink} className="row gap-8">
        <input
          type="email"
          autoFocus
          required
          placeholder="you@team.dev"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={sending}
          className="mono"
          style={{
            flex: 1, background: 'var(--bg-1)', border: '1px solid var(--line)',
            borderRadius: 6, padding: '10px 14px', color: 'var(--fg)', fontSize: 14, outline: 'none',
          }}
        />
        <button type="submit" disabled={sending} className="btn btn-primary">
          {sending ? 'Sending…' : 'Send link'}
        </button>
      </form>

      {err && <p style={{ color: 'var(--err)', fontSize: 13, marginTop: 12 }}>{err}</p>}

      <p className="mono" style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 16, lineHeight: 1.6 }}>
        By signing in you agree to our{' '}
        <a href="/terms" style={{ color: 'var(--fg-2)', textDecoration: 'underline' }}>Terms of Service</a>
        {' '}and{' '}
        <a href="/privacy" style={{ color: 'var(--fg-2)', textDecoration: 'underline' }}>Privacy Policy</a>.
      </p>
    </>
  )
}
